/**
 * @file Dot atom — small filled status circle.
 * @description Narrowed atom for the 8px filled circle that prefixes a status
 * or grouping label (SE-6862, Figma node 80:18295 — the outcome-group marker in
 * the pipeline board's Closed area). The circular shape is the whole point of
 * the atom: the circle-radius utility is budget-counted by a raw substring scan
 * over every feature `*View.tsx`, so a hand-rolled circle in a feature view
 * books new design-system debt. Owning the shape here lets any surface render
 * one without touching that budget.
 *
 * Color is deliberately NOT a prop. The dot's fill is always data-derived (an
 * outcome, a status, a series), and the mapping from datum to semantic token
 * belongs to the feature that owns the datum — the atom just renders whatever
 * background class it is handed, which keeps the closed token vocabulary in the
 * caller's file where lint can see it.
 * @module components/atoms/Dot
 */
import React from "react";

import { Box } from "../Box";

import type { BoxProps } from "../Box";

/**
 * Props for the Dot atom.
 *
 * The accessibility surface is `Pick`ed from the Box atom rather than restated,
 * so it cannot drift from what Box actually accepts. That anchoring is load
 * bearing: `moduleSuffixes` puts `.web` ahead of the bare specifier, so the
 * vendored Box resolves to its web variant — a DOM prop surface — and the only
 * accessibility props available are the ones the Box atom declares by hand.
 * Widening this set means widening Box first.
 */
export interface DotProps extends Pick<
  BoxProps,
  | "accessibilityHint"
  | "accessibilityLabel"
  | "accessibilityLiveRegion"
  | "accessibilityRole"
> {
  /**
   * Fill for the marker, as a semantic background class from the caller's
   * closed vocabulary. Applied verbatim — the atom never interprets it.
   */
  readonly className?: string;
  /** Test/a11y identity is part of the atom API (RFC standing constraint 4). */
  readonly testID?: string;
}

/**
 * Renders an 8px filled circle.
 *
 * Decorative by default: the dot sits beside the label it marks, so it carries
 * no accessible name of its own and assistive tech reads that label instead. A
 * caller that renders one WITHOUT an adjacent label should name it, which is
 * what the forwarded accessibility props are for.
 * @param props - Component props
 * @param props.className - Fill for the marker
 * @param props.testID - Test identifier forwarded to the circle
 * @param props.accessibilityProps - Accessibility props forwarded to the circle
 * @returns The dot element.
 */
const Dot = ({ className = "", testID, ...accessibilityProps }: DotProps) => (
  <Box
    testID={testID}
    className={`size-2 shrink-0 rounded-full ${className}`}
    {...accessibilityProps}
  />
);

Dot.displayName = "Dot";

export { Dot };
